import { Button, FileUpload, Position, ToggleButtonGroup, ToggleOption } from '@consigli/facade';
import {
  ExtendedFile,
  useGetPackagesByTypeQuery,
  useProjectId,
  useStartPipelineMutation,
} from '@consigli/hooks';
import {
  VersionCompareRole,
  DocumentPackageType,
  JobType,
  PipelineName,
  ServiceName,
} from '@consigli/types';
import { FC, useCallback, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BsArrowRight } from 'react-icons/bs';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';

import { Route } from '@/routes';

import { PackageInputMode, VersionPackageSelector } from './version-package-selector';
import { IterationName } from '../components/iteration-name';
import { SelectLanguage } from '../components/select-language';
import { VersionCompareUploadInfo } from '../components/upload-info-panel';
import { useFetchValidateName } from '../components/user-input';
import { TabType, WizardLayout } from '../components/wizard-layout';
import { useCreateInsightPackage } from '../hooks/use-create-insight-package';
import { useUploadProgress } from '../hooks/use-upload-progress';

export enum CommentsMode {
  NO_COMMENTS = 'NO_COMMENTS',
  EMBEDDED_IN_VERSION_A = 'EMBEDDED_IN_VERSION_A',
  SEPARATE_UPLOAD = 'SEPARATE_UPLOAD',
  BOTH = 'BOTH',
}

export const VERSION_COMPARE_SOURCE_TYPES = [
  DocumentPackageType.OFFER,
  DocumentPackageType.TENDER_DOCUMENTS,
  DocumentPackageType.RFP_RFQ_REVIEW,
  DocumentPackageType.MANAGEMENT_OPERATIONS_MAINTENANCE,
  DocumentPackageType.DESIGN_PACKAGE_REVIEW,
  DocumentPackageType.CONTRACT_REVIEW,
];

export const VersionCompareWizard: FC = () => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const navigate = useNavigate();
  const MAX_FILE_SIZE_BYTES = 600 * 1024 * 1024;

  const [isProcessing, setIsProcessing] = useState(false);
  const [showMessagePanel, setShowMessagePanel] = useState(true);
  const [commentRegisterFiles, setCommentRegisterFiles] = useState<ExtendedFile[]>([]);
  const [commentsMode, setCommentsMode] = useState<CommentsMode>(CommentsMode.NO_COMMENTS);
  const [targetCounter, setTargetCounter] = useState(0);
  const [currentCounter, setCurrentCounter] = useState(0);

  // Version A state
  const [primaryMode, setPrimaryMode] = useState<PackageInputMode>(
    PackageInputMode.SELECT_EXISTING,
  );
  const [primaryFiles, setPrimaryFiles] = useState<ExtendedFile[]>([]);

  // Version B state
  const [secondaryMode, setSecondaryMode] = useState<PackageInputMode>(
    PackageInputMode.SELECT_EXISTING,
  );
  const [secondaryFiles, setSecondaryFiles] = useState<ExtendedFile[]>([]);

  const { createDocumentPackage, upload } = useCreateInsightPackage({
    setCurrentCounter,
    setTargetCounter,
  });
  const [startPipeline] = useStartPipelineMutation();

  const { isUploading, progress } = useUploadProgress(currentCounter, targetCounter);

  const methods = useForm<FieldValues>({
    defaultValues: {
      iterationName: '',
      language: '',
      primaryPackageId: null,
      secondaryPackageId: null,
    },
  });

  const { getValues, handleSubmit } = methods;

  const { validateName } = useFetchValidateName(useGetPackagesByTypeQuery, {
    projectId,
    type: DocumentPackageType.VERSION_COMPARE,
  });

  const onSubmit = useCallback(async () => {
    try {
      setIsProcessing(true);

      const iterationName = getValues('iterationName') as string;
      const language = getValues('language') as string;

      // ── Validate primary ───────────────────────────────────────────────
      let primaryPackageId: string | undefined;
      if (primaryMode === PackageInputMode.SELECT_EXISTING) {
        const selected = getValues('primaryPackageId') as string | null;
        if (!selected) {
          toast.error(t('version-compare.no-version-a-selected'));
          return;
        }
        primaryPackageId = selected;
      } else if (primaryFiles.length === 0) {
        toast.error(t('version-compare.no-files-for-version-a'));
        return;
      }

      // ── Validate secondary ─────────────────────────────────────────────
      let secondaryPackageId: string | undefined;
      if (secondaryMode === PackageInputMode.SELECT_EXISTING) {
        const selected = getValues('secondaryPackageId') as string | null;
        if (!selected) {
          toast.error(t('version-compare.no-version-b-selected'));
          return;
        }
        secondaryPackageId = selected;
      } else if (secondaryFiles.length === 0) {
        toast.error(t('version-compare.no-files-for-version-b'));
        return;
      }

      if (primaryPackageId && secondaryPackageId && primaryPackageId === secondaryPackageId) {
        toast.error(t('version-compare.same-package-selected'));
        return;
      }

      // ── Create the VERSION_COMPARE package ────────────────────────────
      const newPackageId = await createDocumentPackage(
        DocumentPackageType.VERSION_COMPARE,
        iterationName,
        language as Parameters<typeof createDocumentPackage>[2],
        undefined,
        undefined,
        undefined,
        {
          ...(primaryPackageId && { primaryPackageId }),
          ...(secondaryPackageId && { secondaryPackageId }),
          commentsMode,
        },
      );

      if (!newPackageId) return;

      // ── Upload files directly to the VERSION_COMPARE package with roles ─
      const uploadWithRole = async (files: ExtendedFile[], role: VersionCompareRole) => {
        setCurrentCounter(0);
        setTargetCounter(files.length);
        await Promise.allSettled(
          files.map((file) =>
            upload(file, newPackageId, undefined, { versionCompareRole: role }).finally(() =>
              setCurrentCounter((count) => count + 1),
            ),
          ),
        );
      };

      if (primaryMode === PackageInputMode.UPLOAD_NEW) {
        await uploadWithRole(primaryFiles, VersionCompareRole.PRIMARY_VERSION);
      }

      if (secondaryMode === PackageInputMode.UPLOAD_NEW) {
        await uploadWithRole(secondaryFiles, VersionCompareRole.SECONDARY_VERSION);
      }

      const hasUploadedCommentRegister =
        commentsMode === CommentsMode.SEPARATE_UPLOAD || commentsMode === CommentsMode.BOTH;

      if (commentRegisterFiles.length > 0 && hasUploadedCommentRegister) {
        await uploadWithRole(commentRegisterFiles, VersionCompareRole.COMMENT_VERSION);
      }

      await startPipeline({
        projectId,
        packageId: newPackageId,
        pipelineName: PipelineName.VERSION_COMPARE,
        jobType: JobType.DOCUMENT_PACKAGE,
        notify: true,
        metadata: {
          ...(primaryPackageId && { primaryPackageId }),
          ...(secondaryPackageId && { secondaryPackageId }),
          versionCompareCommentsPackageId: newPackageId,
          commentsMode,
        },
      }).unwrap();

      toast.success(t('package-creation.success-analyzed'));

      navigate(
        `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${ServiceName.VERSION_COMPARE}/${Route.PACKAGES}/${newPackageId}/${Route.DASHBOARD}`,
      );
    } catch (error) {
      console.log('Error starting version compare:', error);
      toast.error(t('package-creation.failed-analyzed'));
    } finally {
      setIsProcessing(false);
    }
  }, [
    commentRegisterFiles,
    commentsMode,
    createDocumentPackage,
    getValues,
    navigate,
    primaryFiles,
    primaryMode,
    projectId,
    secondaryFiles,
    secondaryMode,
    setCurrentCounter,
    setTargetCounter,
    startPipeline,
    t,
    upload,
  ]);

  const commentsModeOptions: ToggleOption<CommentsMode>[] = [
    { label: t('version-compare.comments-mode.no-comments'), value: CommentsMode.NO_COMMENTS },
    {
      label: t('version-compare.comments-mode.embedded-in-version-a'),
      value: CommentsMode.EMBEDDED_IN_VERSION_A,
    },
    {
      label: t('version-compare.comments-mode.separate-upload'),
      value: CommentsMode.SEPARATE_UPLOAD,
      disabled: true,
    },
    { label: t('version-compare.comments-mode.both'), value: CommentsMode.BOTH, disabled: true },
  ];

  const showCommentRegisterUpload =
    commentsMode === CommentsMode.SEPARATE_UPLOAD || commentsMode === CommentsMode.BOTH;

  const form = (
    <FormProvider {...methods}>
      <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col" translate="no">
          <IterationName
            validateName={validateName}
            label={t('package-creation.analysis-name')}
            errorMessage={`${t('package-creation.analysis-name-empty')}*`}
          />
        </div>

        <SelectLanguage label={t('package-creation.language')} />

        <div className="grid grid-cols-2 items-start gap-4">
          <VersionPackageSelector
            fieldName="primaryPackageId"
            label={t('version-compare.version-a-label')}
            mode={primaryMode}
            onModeChange={setPrimaryMode}
            files={primaryFiles}
            onFilesChange={setPrimaryFiles}
          />
          <VersionPackageSelector
            fieldName="secondaryPackageId"
            label={t('version-compare.version-b-label')}
            mode={secondaryMode}
            onModeChange={setSecondaryMode}
            files={secondaryFiles}
            onFilesChange={setSecondaryFiles}
          />
        </div>

        <div className="mt-4">
          <p className="mb-2 text-base font-semibold text-day-neutral-subtle">
            {t('version-compare.comments-label')}
          </p>
          <ToggleButtonGroup
            options={commentsModeOptions}
            value={commentsMode}
            onChange={setCommentsMode}
          />
        </div>

        {showCommentRegisterUpload && (
          <div className="mt-4">
            <p className="mb-2 text-base font-semibold text-day-neutral-subtle">
              {t('version-compare.comment-register-label')}
            </p>
            <FileUpload
              files={commentRegisterFiles}
              setFiles={setCommentRegisterFiles}
              dropAreaLabel={t('document-upload.drag-n-drop-here')}
              dropAreaClassName="mt-2 h-48 w-full"
              fileButtonLabel={t('document-upload.browse-files')}
              folderButtonLabel={t('document-upload.browse-folders')}
              uploadListLabel={t('document-upload.files')}
              zeroByteFilesMessage={t('document-upload.file-zero-bytes')}
              showMessagePanel={showMessagePanel}
              setShowMessagePanel={setShowMessagePanel}
              maxFileSizeBytes={MAX_FILE_SIZE_BYTES}
              fileSizeWarningText={t('document-upload.file-exceeds-max-size')}
            />
          </div>
        )}

        <div className="mt-6 fc-w-full">
          <Button
            rounded
            primary
            className="w-full fc-px-3 fc-py-2"
            icon={BsArrowRight}
            iconPosition={Position.RIGHT}
            iconColor="white"
            type="submit"
          >
            {t('version-compare.run-comparison')}
          </Button>
        </div>
      </form>
    </FormProvider>
  );

  return (
    <WizardLayout
      isProcessing={isProcessing}
      isUploading={isUploading}
      progress={progress}
      messageHeader={<VersionCompareUploadInfo />}
      tabContent={{
        [TabType.CREATE_PACKAGE]: form,
      }}
    />
  );
};
